import type { Lang } from './languages.js';
import type { LexiconSet, Tier } from './lexicon.js';
import { Rng, hash32 } from './rng.js';
import type { RoundState } from './round.js';
import { requirementOf, submitWord, targetOf, timeLimitOf, stopRound } from './round.js';

/**
 * Training robots. They are deterministic (seeded), never use an LLM, only play valid words
 * from the rack, and are always displayed as robots. Their parameters are explicit
 * hypotheses (vocabulary, response delay, strategy) — also reused by the balance simulator.
 */
export interface BotProfile {
  id: string;
  /** Highest frequency tier the robot knows. */
  maxTier: Tier;
  /** Share (0–1) of the eligible words the robot "knows", decided per word by hashing. */
  knowledge: number;
  /**
   * Optional knowledge per language rank in the player's order (e.g. [0.7, 0.3] = native +
   * learner). Overrides `knowledge` for the listed positions.
   */
  knowledgeByLangRank?: number[];
  /** Delay before each word: base + perTile × tiles, ± jitter. */
  baseDelayMs: number;
  perTileDelayMs: number;
  jitter: number;
  /** Extra delay when switching language compared with the previous word. */
  switchPenaltyMs: number;
  preference: 'short' | 'long' | 'balanced';
  /** Hard cap on words in untimed rounds. */
  maxWords: number;
}

export const BOT_PROFILES: Readonly<Record<string, BotProfile>> = Object.freeze({
  rookie: {
    id: 'rookie',
    maxTier: 1,
    knowledge: 0.45,
    baseDelayMs: 9000,
    perTileDelayMs: 900,
    jitter: 0.35,
    switchPenaltyMs: 3000,
    preference: 'short',
    maxWords: 12,
  },
  casual: {
    id: 'casual',
    maxTier: 2,
    knowledge: 0.45,
    baseDelayMs: 6000,
    perTileDelayMs: 700,
    jitter: 0.3,
    switchPenaltyMs: 2000,
    preference: 'balanced',
    maxWords: 20,
  },
  skilled: {
    id: 'skilled',
    maxTier: 2,
    knowledge: 0.7,
    baseDelayMs: 4200,
    perTileDelayMs: 500,
    jitter: 0.25,
    switchPenaltyMs: 1200,
    preference: 'balanced',
    maxWords: 30,
  },
  expert: {
    id: 'expert',
    maxTier: 3,
    knowledge: 0.6,
    baseDelayMs: 3000,
    perTileDelayMs: 400,
    jitter: 0.2,
    switchPenaltyMs: 800,
    preference: 'long',
    maxWords: 40,
  },
});

export const BOT_LEVELS = ['rookie', 'casual', 'skilled', 'expert'] as const;
export type BotLevel = (typeof BOT_LEVELS)[number];

export interface BotMove {
  atMs: number;
  key: string;
  lang: Lang;
  confirmFinal: boolean;
}

interface Known {
  key: string;
  langs: Lang[];
  tiles: number;
}

/**
 * Plans every move of a robot for a round. The plan only depends on the rack, the languages,
 * the mode, the profile and the seed.
 */
export function planBotRound(
  state: RoundState,
  profile: BotProfile,
  seed: string,
  lexicons: LexiconSet,
): BotMove[] {
  const rng = new Rng(`bot|${profile.id}|${seed}`);
  const langs = state.config.languages;
  const byKey = new Map<string, Known>();
  for (const lang of langs) {
    const lex = lexicons[lang];
    if (!lex) continue;
    for (const i of lex.formableIndices(state.config.rack)) {
      if (lex.tierAt(i) > profile.maxTier) continue;
      const key = lex.keyAt(i);
      const roll = hash32(`${seed}|${profile.id}|${lang}|${key}`) / 4294967296;
      const knowledge = profile.knowledgeByLangRank?.[langs.indexOf(lang)] ?? profile.knowledge;
      if (roll >= knowledge) continue;
      const k = byKey.get(key);
      if (k) k.langs.push(lang);
      else byKey.set(key, { key, langs: [lang], tiles: key.length });
    }
  }
  let pool = [...byKey.values()].sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  const requirement = requirementOf(state);
  const quota = requirement.quotaPerLanguage;
  const target = targetOf(state);
  const limit = timeLimitOf(state);
  const counts = new Map<Lang, number>(langs.map((l) => [l, 0]));
  const needed = (): Lang[] => (quota > 0 ? langs.filter((l) => (counts.get(l) ?? 0) < quota) : []);
  const moves: BotMove[] = [];
  let t = 0;
  let prevLang: Lang | null = null;
  const cap = limit === null ? profile.maxWords : Number.POSITIVE_INFINITY;

  while (pool.length > 0 && moves.length < cap) {
    const need = needed();
    const remainingSlots = target === null ? Infinity : target - moves.length;
    let candidates = pool;
    if (need.length > 0) {
      const helpful = pool.filter((k) => k.langs.some((l) => need.includes(l)));
      // Prioritise the contract when the remaining slots get scarce, or most of the time otherwise.
      if (helpful.length > 0 && (remainingSlots <= need.length * quota || rng.next() < 0.7))
        candidates = helpful;
    }
    const pick = choose(candidates, profile, rng);
    const lang = pick.langs.find((l) => need.includes(l)) ?? pick.langs[0]!;
    const jitter = 1 + (rng.next() * 2 - 1) * profile.jitter;
    let delay = (profile.baseDelayMs + profile.perTileDelayMs * pick.tiles) * jitter;
    if (prevLang !== null && prevLang !== lang) delay += profile.switchPenaltyMs;
    t += Math.round(delay);
    if (limit !== null && t > limit) break;
    const isFinal = target !== null && moves.length + 1 >= target;
    moves.push({ atMs: t, key: pick.key, lang, confirmFinal: isFinal });
    counts.set(lang, (counts.get(lang) ?? 0) + 1);
    prevLang = lang;
    pool = pool.filter((k) => k.key !== pick.key);
    if (isFinal) break;
  }
  return moves;
}

function choose(candidates: Known[], profile: BotProfile, rng: Rng): Known {
  if (profile.preference === 'balanced') return rng.pick(candidates);
  const best =
    profile.preference === 'long'
      ? Math.max(...candidates.map((c) => c.tiles))
      : Math.min(...candidates.map((c) => c.tiles));
  // Mostly follow the preference, sometimes pick anything (humans are not perfect either).
  if (rng.next() < 0.75) return rng.pick(candidates.filter((c) => c.tiles === best));
  return rng.pick(candidates);
}

/** Plays a planned bot round through the real engine (same validation as humans). */
export function playBotRound(
  state: RoundState,
  profile: BotProfile,
  seed: string,
  lexicons: LexiconSet,
): { state: RoundState; moves: BotMove[] } {
  const moves = planBotRound(state, profile, seed, lexicons);
  let s = state;
  for (const m of moves) {
    const r = submitWord(
      s,
      { key: m.key, lang: m.lang, confirmFinal: m.confirmFinal },
      s.startedAt + m.atMs,
      lexicons,
    );
    s = r.state;
    if (s.status === 'finished') break;
  }
  if (s.status !== 'finished') {
    const limit = timeLimitOf(s);
    const last = moves.length > 0 ? moves[moves.length - 1]!.atMs : 0;
    s =
      limit === null
        ? stopRound(s, s.startedAt + last)
        : stopRound(s, s.startedAt + limit + 60_000);
  }
  return { state: s, moves };
}
